import { Spacing } from '@kezios/design-system';
import { Button, Card, Flex, Stack, Text } from '@mantine/core';

import { CheckItem } from '../../atoms/CheckItem/CheckItem';
import css from './PricingCard.module.css';

export type PricingCardProps = {
  title: string;
  variant?: 'center' | 'left' | 'right';
  strikedTitle?: string;
  subtitle?: string;
  badgeText?: string;
  planName: string | React.ReactNode;
  planSubtitle: string | React.ReactNode;
  features: React.ReactElement<typeof CheckItem>[];
  ctaText: string;
  ctaHref?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  width?: number | string;
};

export const PricingCard = ({
  title,
  variant = 'center',
  strikedTitle = undefined,
  subtitle = undefined,
  badgeText = undefined,
  planName,
  planSubtitle,
  features,
  ctaText,
  ctaHref,
  onClick,
  isDisabled = false,
  width = 550,
}: PricingCardProps) => {
  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
      }}
    >
      {badgeText && (
        <Text variant="textMd.semibold" c="primary.6" className={css.badge}>
          {badgeText}
        </Text>
      )}
      <Card
        className={`${css.container} ${isDisabled ? css.disabled : ''}`}
        shadow="lg"
        radius={'xl'}
        px={Spacing.s8}
        w={width}
        h={'100%'}
        withBorder
      >
        <Stack gap={Spacing.s4} mt={Spacing.s4}>
          <Stack
            className={css.header}
            gap={subtitle !== undefined ? 0 : Spacing.s4}
          >
            <Flex gap={Spacing.s4} align="center" justify={variant}>
              {strikedTitle && (
                <Text
                  variant="displayMd.semibold"
                  c="gray.4"
                  style={{ textDecoration: 'line-through' }}
                >
                  {strikedTitle}
                </Text>
              )}
              <Text
                variant="displayLg.semibold"
                c={'gray.9'}
                style={{ textAlign: variant, whiteSpace: 'pre' }}
              >
                {title}
              </Text>
            </Flex>
            {subtitle && (
              <Flex align="center" justify={variant}>
                <Text variant="textXs" c="gray.9">
                  {subtitle}
                </Text>
              </Flex>
            )}
            <Stack gap={Spacing.s1}>
              <Text
                variant="textXl.semibold"
                c={'gray.9'}
                style={{ textAlign: variant }}
              >
                {planName}
              </Text>
              <Text
                variant="textMd"
                c={'gray.6'}
                style={{ textAlign: variant }}
              >
                {planSubtitle}
              </Text>
            </Stack>
          </Stack>

          <Stack gap={Spacing.s4} mt={Spacing.s4} mb={Spacing.s6}>
            {features}
          </Stack>
        </Stack>

        <Button
          component={ctaHref ? 'a' : 'button'}
          href={ctaHref}
          onClick={onClick}
          disabled={isDisabled}
          fullWidth
        >
          {ctaText}
        </Button>
      </Card>
    </div>
  );
};
